
// =========================================================
// LA ESQUINA DEL PARQUÉS
// CARTA DEL BAR · PREMIUM NEON TROPICAL
// CartaBarPage.jsx
// SLIDER DESTACADO + CARTA COMPLETA
// =========================================================

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  ChevronLeft,
  ChevronRight,
  Crown,
  LoaderCircle,
  RefreshCw,
  Search,
  Sparkles,
  UtensilsCrossed,
  Wine,
} from "lucide-react";

import Navbar from "../../components/common/Navbar";
import Footer from "../../components/common/Footer";
import API from "../../services/api";

import catalogBg from "../../assets/bg.png";

import "../../styles/carta-bar.css";

// =========================================================
// CARTA PREDETERMINADA
// =========================================================

const CATEGORIAS_INICIALES = [
  {
    id: "cocteles",
    titulo: "Cócteles",
    productos: [
      ["Mojito Clásico", 18000],
      ["Margarita", 20000],
      ["Piña Colada", 20000],
      ["Sex on the Beach", 22000],
      ["Tequila Sunrise", 20000],
      ["Aperol Spritz", 24000],
      ["Gin Tonic", 22000],
      ["Bloody Mary", 20000],
      ["Daiquiri", 18000],
      ["Cuba Libre", 16000],
    ],
  },
  {
    id: "shots",
    titulo: "Shots",
    productos: [
      ["Tequila", 8000],
      ["Aguardiente", 6000],
      ["Whisky", 10000],
      ["Ron", 8000],
      ["Vodka", 8000],
      ["Jägermeister", 10000],
      ["Fireball", 9000],
      ["Shot Especial de la Casa", 12000],
    ],
  },
  {
    id: "cervezas",
    titulo: "Cervezas",
    productos: [
      ["Club Colombia", 6000],
      ["Corona", 7000],
      ["Heineken", 7000],
      ["Budweiser", 6000],
      ["Stella Artois", 7000],
      ["Modelo", 7000],
      ["Poker", 5000],
      ["Águila", 5000],
    ],
  },
  {
    id: "sin-alcohol",
    titulo: "Bebidas sin alcohol",
    productos: [
      ["Limonada Natural", 5000],
      ["Limonada de Hierbabuena", 6000],
      ["Jugo en Agua", 5000],
      ["Jugo en Leche", 6000],
      ["Gaseosas", 4000],
      ["Té Helado", 5000],
      ["Agua", 3000],
      ["Red Bull", 9000],
    ],
  },
  {
    id: "comidas",
    titulo: "Comidas",
    productos: [
      ["Hamburguesa Clásica", 16000],
      ["Hamburguesa Especial", 20000],
      ["Perro Caliente", 10000],
      ["Alitas BBQ", 18000],
      ["Papas Francesas", 8000],
      ["Nachos con Queso", 16000],
      ["Salchipapas", 12000],
      ["Tequeños", 15000],
    ],
  },
  {
    id: "promociones",
    titulo: "Promociones",
    productos: [
      ["2x1 en Cócteles Clásicos", 36000],
      ["Cubetazo de 6 Cervezas", 28000],
      ["Botella + 4 Shots", 90000],
      ["Alitas + 2 Cervezas", 32000],
      ["Nachos + 2 Cócteles", 36000],
    ],
  },
];

// =========================================================
// UTILIDADES
// =========================================================

function normalizarTexto(valor) {
  return String(valor ?? "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

function precioCOP(valor) {
  const numero = Number(valor);

  return new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    maximumFractionDigits: 0,
  }).format(Number.isFinite(numero) ? numero : 0);
}

function obtenerNombre(producto) {
  return (
    producto?.name ||
    producto?.title ||
    producto?.nombre ||
    "Producto"
  );
}

function obtenerPrecio(producto) {
  const valor =
    producto?.sale_price ??
    producto?.price ??
    producto?.precio ??
    0;

  const numero = Number(valor);

  return Number.isFinite(numero) ? numero : 0;
}

function obtenerCategoria(producto) {
  const categoria =
    producto?.category_name ||
    producto?.category_label ||
    producto?.category ||
    producto?.categoria ||
    "";

  if (typeof categoria === "string") {
    return categoria;
  }

  if (categoria && typeof categoria === "object") {
    return (
      categoria.name ||
      categoria.title ||
      categoria.nombre ||
      ""
    );
  }

  return "";
}

function obtenerImagen(producto) {
  const imagen =
    producto?.image_url ||
    producto?.image ||
    producto?.photo ||
    producto?.picture ||
    producto?.original_image ||
    "";

  if (typeof imagen === "string") {
    return imagen;
  }

  if (imagen && typeof imagen === "object") {
    return imagen.url || imagen.image || "";
  }

  return "";
}

function obtenerListaProductos(respuesta) {
  const data = respuesta?.data;

  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.products)) return data.products;
  if (Array.isArray(data?.items)) return data.items;

  return [];
}

// =========================================================
// ASOCIAR PRODUCTOS DE DJANGO A LAS CATEGORÍAS DE LA CARTA
// =========================================================

const PALABRAS_CATEGORIA = {
  cocteles: ["coctel", "cocktail"],
  shots: ["shot", "licor", "licores"],
  cervezas: ["cerveza", "beer"],
  "sin-alcohol": [
    "sin alcohol",
    "bebida",
    "jugo",
    "limonada",
    "gaseosa",
  ],
  comidas: [
    "comida",
    "hamburguesa",
    "perro",
    "alitas",
    "papas",
    "nachos",
    "salchipapa",
    "tequeno",
  ],
  promociones: ["promocion", "combo"],
};

function combinarCartaConProductos(productosAPI) {
  return CATEGORIAS_INICIALES.map((categoria) => {
    if (!productosAPI.length) {
      return categoria;
    }

    const palabras = PALABRAS_CATEGORIA[categoria.id] || [];

    const encontrados = productosAPI.filter((producto) => {
      const nombreCategoria = normalizarTexto(
        obtenerCategoria(producto)
      );

      const nombreProducto = normalizarTexto(
        obtenerNombre(producto)
      );

      return (
        palabras.some((palabra) =>
          nombreCategoria.includes(normalizarTexto(palabra))
        ) ||
        (categoria.id === "promociones" &&
          nombreProducto.includes("combo"))
      );
    });

    if (!encontrados.length) {
      return categoria;
    }

    return {
      ...categoria,
      productos: encontrados.map((producto) => [
        obtenerNombre(producto),
        obtenerPrecio(producto),
      ]),
    };
  });
}

// =========================================================
// TARJETA DESTACADA DEL SLIDER
// =========================================================

function ProductoSlider({ producto, indice }) {
  const [imagenError, setImagenError] = useState(false);

  const esComida = producto.categoriaId === "comidas";

  return (
    <article
      className="carta-slide-card"
      aria-roledescription="diapositiva"
      aria-label={`${producto.nombre}, producto ${indice + 1}`}
    >
      <div className="carta-slide-image">
        {producto.imagen && !imagenError ? (
          <img
            src={producto.imagen}
            alt={producto.nombre}
            loading={indice === 0 ? "eager" : "lazy"}
            onError={() => setImagenError(true)}
          />
        ) : (
          <div className="carta-slide-placeholder">
            {esComida ? (
              <UtensilsCrossed size={64} />
            ) : (
              <Wine size={64} />
            )}

            <span>LA ESQUINA</span>
            <small>DEL PARQUÉS</small>
          </div>
        )}

        <span className="carta-slide-image-shade" />

        <div className="carta-slide-topline">
          <span className="carta-slide-brand">
            <Crown size={15} />
            LA ESQUINA DEL PARQUÉS
          </span>

          <span className="carta-slide-number">
            {String(indice + 1).padStart(2, "0")}
            <span>
              {" "}
              / {String(producto.totalSlides || 1).padStart(2, "0")}
            </span>
          </span>
        </div>

        <div className="carta-slide-info">
          <span className="carta-slide-tag">
            <Sparkles size={14} />
            DESTACADO DE LA CARTA
          </span>

          <span className="carta-slide-category">
            {producto.categoria}
          </span>

          <h3>{producto.nombre}</h3>

          <p className="carta-slide-description">
            Descubre nuestros sabores, disfruta el momento
            y comparte una experiencia en La Esquina del Parqués.
          </p>

          <div className="carta-slide-bottom">
            <div className="carta-slide-price">
              <span>PRECIO</span>
              <strong>{precioCOP(producto.precio)}</strong>
            </div>

            <a
              href={`#${producto.categoriaId}`}
              onClick={(event) => {
                event.preventDefault();

                document
                  .getElementById(producto.categoriaId)
                  ?.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                  });
              }}
            >
              VER EN LA CARTA
              <ChevronRight size={18} />
            </a>
          </div>
        </div>
      </div>
    </article>
  );
}

// =========================================================
// TARJETA DE CATEGORÍA
// =========================================================

function CategoriaCarta({ categoria, indice, filtro }) {
  const filtroNormalizado = normalizarTexto(filtro);

  const productosFiltrados = categoria.productos.filter(
    ([nombre]) =>
      normalizarTexto(nombre).includes(filtroNormalizado)
  );

  if (productosFiltrados.length === 0) {
    return null;
  }

  return (
    <article
      id={categoria.id}
      className={`carta-card carta-card-${categoria.id}`}
      style={{ "--card-index": indice }}
    >
      <div className="carta-card-heading">
        <span className="carta-crown" aria-hidden="true">
          <Crown size={26} strokeWidth={1.7} />
        </span>

        <div>
          <h2>{categoria.titulo}</h2>

          <span className="carta-category-count">
            {productosFiltrados.length} opciones
          </span>
        </div>
      </div>

      <ul className="carta-productos">
        {productosFiltrados.map(([nombre, precio], index) => (
          <li key={`${categoria.id}-${nombre}-${index}`}>
            <span className="carta-producto-nombre">
              {nombre}
            </span>

            <span className="carta-puntos" aria-hidden="true" />

            <strong>{precioCOP(precio)}</strong>
          </li>
        ))}
      </ul>
    </article>
  );
}

// =========================================================
// PÁGINA PRINCIPAL
// =========================================================

export default function CartaBarPage() {
  const [productosAPI, setProductosAPI] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorAPI, setErrorAPI] = useState("");
  const [filtro, setFiltro] = useState("");
  const [slideActual, setSlideActual] = useState(0);

  const sliderRef = useRef(null);

  // -------------------------------------------------------
  // CARGAR PRODUCTOS DESDE DJANGO
  // -------------------------------------------------------

  const cargarProductos = useCallback(async () => {
    try {
      setLoading(true);
      setErrorAPI("");

      const respuesta = await API.get("/products/");

      setProductosAPI(obtenerListaProductos(respuesta));
    } catch (error) {
      console.error("Error cargando la carta:", error);

      setErrorAPI(
        "No pudimos actualizar la carta. Puedes consultar las opciones disponibles."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    cargarProductos();
  }, [cargarProductos]);

  // -------------------------------------------------------
  // CONSTRUIR LA CARTA
  // -------------------------------------------------------

  const carta = useMemo(
    () => combinarCartaConProductos(productosAPI),
    [productosAPI]
  );

  // -------------------------------------------------------
  // PRODUCTOS DEL SLIDER
  // -------------------------------------------------------

  const productosDestacados = useMemo(() => {
    const destacados = [];

    const maximo = Math.max(
      0,
      ...carta.map((categoria) => categoria.productos.length)
    );

    for (let indice = 0; indice < maximo; indice += 1) {
      carta.forEach((categoria) => {
        const item = categoria.productos[indice];

        if (!item) return;

        const [nombre, precio] = item;

        const productoAPI = productosAPI.find(
          (producto) =>
            normalizarTexto(obtenerNombre(producto)) ===
            normalizarTexto(nombre)
        );

        destacados.push({
          nombre,
          precio,
          categoria: categoria.titulo,
          categoriaId: categoria.id,
          imagen: productoAPI ? obtenerImagen(productoAPI) : "",
        });
      });
    }

    return destacados.map((producto, indice) => ({
      ...producto,
      totalSlides: destacados.length,
      indice,
    }));
  }, [carta, productosAPI]);

  // Reiniciar el slider cuando cambian sus productos.
  useEffect(() => {
    setSlideActual(0);

    if (sliderRef.current) {
      sliderRef.current.scrollTo({
        left: 0,
        behavior: "auto",
      });
    }
  }, [productosDestacados.length]);

  // -------------------------------------------------------
  // NAVEGACIÓN DEL SLIDER
  // -------------------------------------------------------

  const irASlide = useCallback((indice) => {
    const contenedor = sliderRef.current;

    if (!contenedor) return;

    const tarjetas = contenedor.querySelectorAll(
      ".carta-slide-card"
    );

    if (!tarjetas.length) return;

    const destino = Math.max(
      0,
      Math.min(indice, tarjetas.length - 1)
    );

    const tarjeta = tarjetas[destino];

    contenedor.scrollTo({
      left: tarjeta.offsetLeft - tarjetas[0].offsetLeft,
      behavior: "smooth",
    });

    setSlideActual(destino);
  }, []);

  const moverSlider = useCallback(
    (direccion) => {
      const total = productosDestacados.length;

      if (total <= 1) return;

      const siguiente =
        (slideActual + direccion + total) % total;

      irASlide(siguiente);
    },
    [irASlide, productosDestacados.length, slideActual]
  );

  const actualizarSlide = useCallback(() => {
    const contenedor = sliderRef.current;

    if (!contenedor) return;

    const tarjetas = contenedor.querySelectorAll(
      ".carta-slide-card"
    );

    if (!tarjetas.length) return;

    const posicion = contenedor.scrollLeft;
    const primeraPosicion = tarjetas[0].offsetLeft;

    let indiceMasCercano = 0;
    let distanciaMinima = Infinity;

    tarjetas.forEach((tarjeta, indice) => {
      const distancia = Math.abs(
        tarjeta.offsetLeft - primeraPosicion - posicion
      );

      if (distancia < distanciaMinima) {
        distanciaMinima = distancia;
        indiceMasCercano = indice;
      }
    });

    setSlideActual((actual) =>
      actual === indiceMasCercano ? actual : indiceMasCercano
    );
  }, []);

  useEffect(() => {
    window.addEventListener("resize", actualizarSlide);

    return () => {
      window.removeEventListener("resize", actualizarSlide);
    };
  }, [actualizarSlide]);

  // -------------------------------------------------------
  // CONTADORES
  // -------------------------------------------------------

  const puntosVisibles = Math.min(productosDestacados.length, 8);

  // -------------------------------------------------------
  // RENDER
  // -------------------------------------------------------

  return (
    <>
      <Navbar />

      <main
        className="carta-bar-page"
        style={{
          "--carta-bg": `url("${catalogBg}")`,
        }}
      >
        <div className="carta-background-glow" />

        {/* HERO DE LA CARTA DEL BAR */}

        <section
          className="carta-hero"
          style={{ backgroundImage: `url("${catalogBg}")` }}
        >
          <div className="carta-hero-overlay" />
          <div className="carta-hero-glow" />

          <div className="carta-hero-content">
            <div className="carta-hero-breadcrumb">
              <span>LA ESQUINA DEL PARQUÉS</span>
              <span className="carta-hero-separator">/</span>
              <span>NUESTRA CARTA</span>
            </div>

            <span className="carta-hero-eyebrow">
              <span className="carta-hero-eyebrow-line" />
              SABORES QUE SE DISFRUTAN
            </span>

            <h1>
              Una buena historia
              <br />
              comienza con algo
              <br />
              <em>delicioso.</em>
            </h1>

            <p className="carta-hero-description">
              Descubre nuestra selección de bebidas, comidas y promociones,
              preparada para acompañar esos momentos que merecen quedarse
              en la memoria.
            </p>

            <div className="carta-hero-actions">
              <a href="#carta-menu" className="carta-hero-primary">
                Explorar la carta
                <span aria-hidden="true">↗</span>
              </a>

              <a
                href="#carta-destacados"
                className="carta-hero-secondary"
              >
                Descubrir favoritos
                <span aria-hidden="true">↓</span>
              </a>
            </div>

            <div className="carta-hero-bottom">
              <span className="carta-hero-bottom-dot" />
              BUENOS SABORES · BUENOS MOMENTOS
            </div>
          </div>

          <div className="carta-hero-side-label">
            CARTA DEL BAR
          </div>

          <div className="carta-hero-scroll">
            <span />
            DESLIZA PARA DESCUBRIR
          </div>
        </section>

        {/* ACCESOS RÁPIDOS A CADA CATEGORÍA */}

        <nav
          className="carta-shortcuts"
          aria-label="Categorías de la carta del bar"
        >
          {carta.map((categoria) => (
            <a key={categoria.id} href={`#${categoria.id}`}>
              {categoria.titulo}
            </a>
          ))}
        </nav>

        {/* AVISO DE CONEXIÓN CON LA API */}

        {errorAPI && (
          <div className="carta-api-notice" role="status">
            <span>{errorAPI}</span>

            <button
              type="button"
              onClick={cargarProductos}
              disabled={loading}
            >
              {loading ? (
                <LoaderCircle
                  size={15}
                  className="carta-spin"
                />
              ) : (
                <RefreshCw size={15} />
              )}

              Reintentar
            </button>
          </div>
        )}

        {/* SLIDER DE PRODUCTOS DESTACADOS */}

        <section
          id="carta-destacados"
          className="carta-featured"
          aria-label="Productos destacados de la carta del bar"
        >
          <div className="carta-section-heading">
            <div>
              <span className="carta-section-eyebrow">
                BEBIDAS, COMIDAS Y PROMOCIONES
              </span>

              <h2>
                DESTACADOS <span>DE LA CARTA</span>
              </h2>

              <p>
                Explora algunas de las opciones disponibles
                en La Esquina del Parqués.
              </p>
            </div>

            <div className="carta-slider-controls">
              <button
                type="button"
                onClick={() => moverSlider(-1)}
                aria-label="Producto anterior"
                disabled={
                  loading || productosDestacados.length <= 1
                }
              >
                <ChevronLeft size={22} />
              </button>

              <button
                type="button"
                onClick={() => moverSlider(1)}
                aria-label="Producto siguiente"
                disabled={
                  loading || productosDestacados.length <= 1
                }
              >
                <ChevronRight size={22} />
              </button>
            </div>
          </div>

          {loading && productosDestacados.length === 0 ? (
            <div className="carta-slider-loading" role="status">
              <LoaderCircle
                className="carta-spin"
                size={26}
              />
              <span>Cargando la carta...</span>
            </div>
          ) : (
            <>
              <div
                className="carta-slider-viewport"
                ref={sliderRef}
                onScroll={actualizarSlide}
                tabIndex={0}
                aria-label="Desliza horizontalmente para explorar los productos de la carta"
              >
                <div className="carta-slider-track">
                  {productosDestacados.map((producto, indice) => (
                    <ProductoSlider
                      key={`${producto.categoriaId}-${producto.nombre}`}
                      producto={producto}
                      indice={indice}
                    />
                  ))}
                </div>
              </div>

              {productosDestacados.length === 0 && (
                <div className="carta-empty">
                  No hay productos destacados disponibles.
                </div>
              )}

              {productosDestacados.length > 1 && (
                <div className="carta-slider-footer">
                  <div
                    className="carta-slider-dots"
                    aria-label="Seleccionar producto destacado"
                  >
                    {productosDestacados
                      .slice(0, puntosVisibles)
                      .map((producto, indice) => (
                        <button
                          key={`${producto.categoriaId}-${producto.nombre}-dot`}
                          type="button"
                          className={
                            slideActual === indice ? "active" : ""
                          }
                          onClick={() => irASlide(indice)}
                          aria-label={`Ver ${producto.nombre}`}
                          aria-current={
                            slideActual === indice ? "true" : undefined
                          }
                        />
                      ))}
                  </div>

                  <span className="carta-slider-counter">
                    {String(slideActual + 1).padStart(2, "0")}
                    <span> / </span>
                    {String(productosDestacados.length).padStart(2, "0")}
                  </span>
                </div>
              )}
            </>
          )}
        </section>

        {/* BUSCADOR Y CARTA COMPLETA */}

        <section
          id="carta-menu"
          className="carta-menu-section"
          aria-label="Carta completa del bar"
        >
          <div className="carta-menu-topbar">
            <div className="carta-menu-title">
              <span className="carta-section-eyebrow">
                BEBIDAS · COMIDAS · PROMOCIONES
              </span>

              <h2>
                NUESTRA <span>CARTA</span>
              </h2>

              <p>
                Busca tus favoritos y consulta las opciones
                por categoría.
              </p>
            </div>

            <label className="carta-search">
              <Search size={19} aria-hidden="true" />

              <input
                type="search"
                value={filtro}
                onChange={(event) => setFiltro(event.target.value)}
                placeholder="Buscar bebidas, comidas..."
                aria-label="Buscar productos de la carta"
              />

              {filtro && (
                <button
                  type="button"
                  onClick={() => setFiltro("")}
                  aria-label="Limpiar búsqueda"
                >
                  ×
                </button>
              )}
            </label>
          </div>

          <div className="carta-menu">
            {carta.map((categoria, indice) => (
              <CategoriaCarta
                key={categoria.id}
                categoria={categoria}
                indice={indice}
                filtro={filtro}
              />
            ))}
          </div>

          {filtro &&
            !carta.some((categoria) =>
              categoria.productos.some(([nombre]) =>
                normalizarTexto(nombre).includes(
                  normalizarTexto(filtro)
                )
              )
            ) && (
              <div className="carta-empty">
                No encontramos productos para “{filtro}”.

                <button
                  type="button"
                  onClick={() => setFiltro("")}
                >
                  Ver toda la carta
                </button>
              </div>
            )}
        </section>
      </main>

      <Footer />
    </>
  );
}
